import { useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon, InfoIcon } from 'lucide-react'
import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material'

import { Card } from '@repo/ui'

export interface HowItWorksStep {
  title: string
  body: string
}

const readOpen = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}

/**
 * The collapsible "How this works" card at the top of an `/internal/analytics` tab: closed by
 * default, remembered per browser, a grid of short titled steps and a footer note.
 *
 * Extracted when the Affiliates tab (VRG-665) needed the card the SEO & AEO tab (VRG-643)
 * already drew — the second hand-roll is the trigger (working-conventions §36). Each tab owns
 * its words; this owns the shape, the toggle and the remembered state.
 */
export const HowItWorksCard = ({
  id,
  storageKey,
  summary,
  steps,
  footer,
}: {
  /** DOM id of the collapsible region, for `aria-controls`. Unique on the page. */
  id: string
  /** localStorage key for the open/closed state. */
  storageKey: string
  /** One line beside the title, hidden on phones. */
  summary: string
  steps: ReadonlyArray<HowItWorksStep>
  footer?: React.ReactNode
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(() => readOpen(storageKey))
  const toggle = () => {
    const next = !open
    setOpen(next)
    try {
      window.localStorage.setItem(storageKey, next ? '1' : '0')
    } catch {
      /* storage blocked: the panel still works, it just forgets */
    }
  }
  return (
    <Card>
      <Box
        component="button"
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={id}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.brand[500]}`,
            outlineOffset: 4,
            borderRadius: 1,
          },
        }}
      >
        <InfoIcon size={18} strokeWidth={1.5} color={theme.palette.brand[500]} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          How this works
        </Typography>
        <Typography
          variant="body3"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          {summary}
        </Typography>
        {open ? <ChevronUpIcon size={18} /> : <ChevronDownIcon size={18} />}
      </Box>
      <Collapse in={open} id={id}>
        <Stack spacing={4} sx={{ mt: 4 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 4,
            }}
          >
            {steps.map((s) => (
              <Stack key={s.title} spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {s.title}
                </Typography>
                <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {s.body}
                </Typography>
              </Stack>
            ))}
          </Box>
          {footer}
        </Stack>
      </Collapse>
    </Card>
  )
}
